import type { ReactiveController, ReactiveControllerHost } from 'lit';

/** Fill mode for WAAPI (KeyframeEffectOptions). */
export type FillMode = 'none' | 'forwards' | 'backwards' | 'both';

/** Playback direction for WAAPI (KeyframeEffectOptions). */
export type AnimationDirection =
  'normal' | 'reverse' | 'alternate' | 'alternate-reverse';

/**
 * Options for a single animation run (Web Animations API).
 * @see https://developer.mozilla.org/en-US/docs/Web/API/KeyframeEffect/KeyframeEffect#options
 */
export interface AnimationOptions {
  /** Duration in milliseconds. */
  duration?: number;
  /** Delay before start in milliseconds. */
  delay?: number;
  /** Easing function (CSS easing or cubic-bezier). */
  easing?: string;
  /** Fill mode: 'none' | 'forwards' | 'backwards' | 'both'. */
  fill?: FillMode;
  /** Number of iterations (default 1). */
  iterations?: number;
  /** Playback direction. */
  direction?: AnimationDirection;
  /** End delay in milliseconds. */
  endDelay?: number;
  /** Unique id for the animation (useful for canceling by id). */
  id?: string;
}

/**
 * Keyframes format accepted by Element.animate().
 * @see https://developer.mozilla.org/en-US/docs/Web/API/Web_Animations_API/Keyframe_Formats
 */
export type AnimationKeyframes = Keyframe[] | PropertyIndexedKeyframes;

/**
 * Configuration for the controller defaults (used when not overridden per call).
 */
export interface AnimationControllerConfig {
  defaultDuration?: number;
  defaultEasing?: string;
  defaultFill?: FillMode;
}

/** Direction for slide presets. */
export type SlideDirection = 'up' | 'down' | 'left' | 'right';

/**
 * AnimationController – consistent animations via the Web Animations API (WAAPI).
 *
 * Use as a Lit ReactiveController so components get:
 * - Shared defaults (duration, easing, fill)
 * - Preset helpers: fadeIn, fadeOut, slideIn, slideOut, scaleIn, scaleOut
 * - Low-level animate() for custom keyframes
 * - Automatic cancellation of running animations when the host disconnects
 *
 * @example
 * ```ts
 * class MyPanel extends LitElement {
 *   animation = new AnimationController(this, { defaultDuration: 200, defaultEasing: 'ease-out' });
 *
 *   async open() {
 *     await this.animation.fadeIn(this.panelEl);
 *   }
 *
 *   async close() {
 *     await this.animation.fadeOut(this.panelEl);
 *   }
 * }
 * ```
 */
export class AnimationController implements ReactiveController {
  readonly host: ReactiveControllerHost;
  private readonly config: Required<AnimationControllerConfig>;
  private readonly running = new Set<Animation>();

  constructor(
    host: ReactiveControllerHost,
    config: AnimationControllerConfig = {}
  ) {
    this.host = host;
    this.config = {
      defaultDuration: config.defaultDuration ?? 200,
      defaultEasing: config.defaultEasing ?? 'ease-out',
      defaultFill: config.defaultFill ?? 'forwards',
    };
    host.addController(this);
  }

  hostDisconnected(): void {
    this.running.forEach((a) => {
      try {
        a.cancel();
      } catch {
        // ignore
      }
    });
    this.running.clear();
  }

  /**
   * Run a WAAPI animation on an element. Returns the Animation for chaining or await .finished.
   * Running animations are tracked and cancelled when the host disconnects.
   */
  animate(
    element: Element | null | undefined,
    keyframes: AnimationKeyframes,
    options: AnimationOptions = {}
  ): Animation | null {
    if (!element) return null;

    const duration = options.duration ?? this.config.defaultDuration;
    const easing = options.easing ?? this.config.defaultEasing;
    const fill = options.fill ?? this.config.defaultFill;

    const opts: KeyframeAnimationOptions = {
      duration,
      delay: options.delay ?? 0,
      easing,
      fill,
      iterations: options.iterations ?? 1,
      direction: options.direction ?? 'normal',
      endDelay: options.endDelay ?? 0,
      id: options.id,
    };

    const animation = element.animate(keyframes as Keyframe[], opts);
    this.running.add(animation);
    animation.finished
      .then(() => this.running.delete(animation))
      .catch(() => this.running.delete(animation));
    return animation;
  }

  /**
   * Fade in: opacity 0 → 1.
   */
  fadeIn(
    element: Element | null | undefined,
    options: AnimationOptions = {}
  ): Animation | null {
    return this.animate(element, [{ opacity: 0 }, { opacity: 1 }], options);
  }

  /**
   * Fade out: opacity 1 → 0.
   */
  fadeOut(
    element: Element | null | undefined,
    options: AnimationOptions = {}
  ): Animation | null {
    return this.animate(element, [{ opacity: 1 }, { opacity: 0 }], options);
  }

  /**
   * Slide in from the given direction (translate from off-screen to 0).
   */
  slideIn(
    element: Element | null | undefined,
    options: AnimationOptions & { direction?: SlideDirection } = {}
  ): Animation | null {
    const dir = options.direction ?? 'down';
    const [fromX, fromY] = slideOffset(dir, true);
    const keyframes: Keyframe[] = [
      { transform: `translate(${fromX}, ${fromY})` },
      { transform: 'translate(0, 0)' },
    ];
    return this.animate(element, keyframes, options);
  }

  /**
   * Slide out toward the given direction (translate from 0 to off-screen).
   */
  slideOut(
    element: Element | null | undefined,
    options: AnimationOptions & { direction?: SlideDirection } = {}
  ): Animation | null {
    const dir = options.direction ?? 'up';
    const [toX, toY] = slideOffset(dir, false);
    const keyframes: Keyframe[] = [
      { transform: 'translate(0, 0)' },
      { transform: `translate(${toX}, ${toY})` },
    ];
    return this.animate(element, keyframes, options);
  }

  /**
   * Scale in: scale(0) or scale(0.9) → scale(1). Optionally combine with opacity.
   */
  scaleIn(
    element: Element | null | undefined,
    options: AnimationOptions & { fromScale?: number; opacity?: boolean } = {}
  ): Animation | null {
    const from = options.fromScale ?? 0.95;
    const withOpacity = options.opacity !== false;
    const keyframes: Keyframe[] = withOpacity
      ? [
          { transform: `scale(${from})`, opacity: 0 },
          { transform: 'scale(1)', opacity: 1 },
        ]
      : [{ transform: `scale(${from})` }, { transform: 'scale(1)' }];
    return this.animate(element, keyframes, options);
  }

  /**
   * Scale out: scale(1) → scale(0.95) or custom. Optionally combine with opacity.
   */
  scaleOut(
    element: Element | null | undefined,
    options: AnimationOptions & { toScale?: number; opacity?: boolean } = {}
  ): Animation | null {
    const to = options.toScale ?? 0.95;
    const withOpacity = options.opacity !== false;
    const keyframes: Keyframe[] = withOpacity
      ? [
          { transform: 'scale(1)', opacity: 1 },
          { transform: `scale(${to})`, opacity: 0 },
        ]
      : [{ transform: 'scale(1)' }, { transform: `scale(${to})` }];
    return this.animate(element, keyframes, options);
  }

  /**
   * Cancel a specific animation (e.g. one you stored from animate() or a preset).
   */
  cancel(animation: Animation): void {
    animation.cancel();
    this.running.delete(animation);
  }

  /**
   * Cancel all animations currently running for this controller.
   */
  cancelAll(): void {
    this.running.forEach((a) => {
      try {
        a.cancel();
      } catch {
        // ignore
      }
    });
    this.running.clear();
  }
}

function slideOffset(
  direction: SlideDirection,
  isIn: boolean
): [string, string] {
  const pct = '100%';
  switch (direction) {
    case 'up':
      return ['0', isIn ? pct : `-${pct}`];
    case 'down':
      return ['0', isIn ? `-${pct}` : pct];
    case 'left':
      return [isIn ? pct : `-${pct}`, '0'];
    case 'right':
      return [isIn ? `-${pct}` : pct, '0'];
    default:
      return ['0', isIn ? `-${pct}` : pct];
  }
}
